import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { RadioButtonGroup } from '~/components/RadioButtonGroup/RadioButtonGroup';
import { cn } from '~/lib/cn';
import { chevron } from '~/modules/artist-roster/components/TrackDetails/components/DateRangePicker/DateRangePicker.styles';

const OBJECTIVES_VALUES = ['awareness', 'engagement', 'response'];

interface ObjectivePickerProps {
  value?: string;
  onChange: (value: string) => void;
}

export function ObjectivePicker({
  onChange,
  value = 'awareness',
}: ObjectivePickerProps) {
  const { t } = useTranslation('campaign-planner', {
    keyPrefix: 'benchmarks',
  });

  // States
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          aria-label="objective-picker-trigger"
          className="relative flex w-full items-center gap-sm rounded-sm border py-sm pl-md pr-2xl transition-colors duration-100 bg-emphasis-default border-emphasis-subtle text-inverse hover:bg-emphasis-strong"
        >
          <span className="text-white">{t(`objectives.${value}`)}</span>
          <Icon
            className={cn(chevron({ state: open ? 'open' : 'default' }))}
            size="md"
            type="chevron-down"
          />
        </button>
      </Popover.Trigger>

      <Popover.Content
        side="bottom"
        align="start"
        className="z-10 mt-xs w-[--radix-popover-trigger-width] rounded-sm border bg-level-01 bg-level-03 border-emphasis-subtle"
      >
        <div className="flex flex-col gap-md p-md">
          <RadioButtonGroup
            items={OBJECTIVES_VALUES.map((item) => ({
              value: item,
              labelComponent: (
                <div className="flex flex-col items-start gap-xs" key={item}>
                  <span className="font-bold">{t(`objectives.${item}`)}</span>
                  <span>{t(`objectives.${item}Caption`)}</span>
                </div>
              ),
              labelAlign: 'start',
            }))}
            itemClassName="gap-lg p-md hover:bg-emphasis-strong bg-emphasis-default duration-100"
            itemSelectedClassName="border-emphasis-stronger"
            defaultValue={value}
            onSelect={onChange}
          />
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

ObjectivePicker.displayName = 'ObjectivePicker';
